"use client";

import { useEffect, useRef } from "react";

const FINEPOINTER_QUERY = "(hover: hover) and (pointer: fine)";

/**
 * Cursor probe for the hero. It draws the crosshair, reports where the probe is
 * sitting, and hands the same pointer position to the hero surface, which is
 * where the displacement happens — one signal, two jobs.
 * Pointer-only, so touch devices never see it.
 */
export const HeroProbe = () => {
  const probeRef = useRef<HTMLDivElement>(null);
  const readoutXRef = useRef<HTMLSpanElement>(null);
  const readoutYRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const probe = probeRef.current;
    const root = probe?.closest<HTMLElement>("[data-hero-root]");
    if (!probe || !root) return;

    let frame: number | null = null;
    let pending: { x: number; y: number } | null = null;
    const finePointer = window.matchMedia(FINEPOINTER_QUERY);

    const write = () => {
      frame = null;
      if (!pending) return;
      probe.style.setProperty("--ix", `${pending.x}px`);
      probe.style.setProperty("--iy", `${pending.y}px`);
      const x = String(Math.round(pending.x));
      const y = String(Math.round(pending.y));
      if (readoutXRef.current) readoutXRef.current.textContent = x;
      if (readoutYRef.current) readoutYRef.current.textContent = y;
    };

    const handleMove = (event: PointerEvent) => {
      const rect = root.getBoundingClientRect();
      pending = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      frame ??= requestAnimationFrame(write);
    };

    const handleEnter = () => {
      probe.style.opacity = "1";
    };

    const handleLeave = () => {
      probe.style.opacity = "0";
    };

    if (finePointer.matches) {
      root.addEventListener("pointermove", handleMove);
      root.addEventListener("pointerenter", handleEnter);
      root.addEventListener("pointerleave", handleLeave);
    }

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      root.removeEventListener("pointermove", handleMove);
      root.removeEventListener("pointerenter", handleEnter);
      root.removeEventListener("pointerleave", handleLeave);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-10 opacity-0 transition-opacity duration-300"
      ref={probeRef}
    >
      <span className="absolute inset-y-0 w-px bg-primary/30" style={{ left: "var(--ix, 0px)" }} />
      <span className="absolute inset-x-0 h-px bg-primary/30" style={{ top: "var(--iy, 0px)" }} />
      <span
        className="absolute size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary"
        style={{ left: "var(--ix, 0px)", top: "var(--iy, 0px)" }}
      />
      {/* Fixed-width slots keep the chip from jittering as the digits change. */}
      <span
        className="absolute flex -translate-y-1/2 translate-x-3 items-center rounded-[3px] border border-primary/40 bg-void/85 px-2 py-1 font-daylight-mono text-[0.625rem] leading-none tabular-nums text-primary"
        style={{ left: "var(--ix, 0px)", top: "var(--iy, 0px)" }}
      >
        <span className="min-w-[4ch] text-right" ref={readoutXRef}>
          0
        </span>
        <span className="px-1 text-primary/45">:</span>
        <span className="min-w-[3ch] text-left" ref={readoutYRef}>
          0
        </span>
      </span>
    </div>
  );
};

HeroProbe.displayName = "Content::HeroProbe";
